import { useForm } from "react-hook-form"
import { CreateTask } from "../types/task";
import { useEffect, useRef, useState } from "react";
import { useCreateTaskMutation } from "../api/tareas/tareaApi";
import { CircularProgress } from "@mui/joy";

interface Props {
    empresaId: number;
}

export function CreateTaskForm({ empresaId }: Props) {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [modalPosition, setModalPosition] = useState<{ top: number; left: number; }>({
        top: 0,
        left: 0,
    });
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, },
    } = useForm<CreateTask>({ defaultValues: { enableSendNotification: true }})

    const [createTask, { isLoading }] = useCreateTaskMutation();

    const submitTask = handleSubmit(async (data: CreateTask) => {
        try {
            data.horaInicio = `${data.horaInicio}:00`;
            data.horaFin = data.horaFin && data.horaFin?.length > 0 ? `${data.horaFin}:00` : null;
            data.fechaFin = data.fechaFin && data.fechaFin.length > 0 ? data.fechaFin : null;

            data.empresaId = empresaId
            console.log(data);

            await createTask(data).unwrap()

            setIsOpen(false);
            reset();
            
        } catch (error) {
            console.error(error);
        }
    });

    const toggleModal = () => {
        if(buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setModalPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX - 280,
            });
        }

        setIsOpen(!isOpen);
        reset();
    }

    const handleClickOutside = (event: MouseEvent) => {
        if(modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        buttonRef.current && 
        !buttonRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    }

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        }
    }, []);

    return (
        <>
        <button 
            ref={buttonRef}
            onClick={toggleModal}
            className="text-sm hover:underline hover:text-blue-800">
            Crear tarea
        </button>
        {isOpen && (
        <div
            ref={modalRef}
            className="absolute bg-white shadow-lg border border-gray-300 rounded py-3 px-5 z-50"
            style={{ top: modalPosition.top, left: modalPosition.left }}>

            <form onSubmit={submitTask} >
                <div className="flex flex-col">
                    <LabelTaskForm>Titulo</LabelTaskForm>
                    <input 
                        {...register("titulo", {
                            required: "La tarea necesita un titulo.",
                            maxLength: 40,
                        })}
                        className="
                            border-gray-400 focus:outline-none focus:border-gray-800 pl-2
                            focus:border-2 border rounded text-gray-600 font-normal hover:border-2"
                        autoComplete="off"
                        type="text" />
                </div>

                {errors.titulo?.type === "required" && (
                    <p className="valErrorText">{errors.titulo.message}</p>
                )}
                {errors.titulo?.type === "maxLength" && (
                    <p className="valErrorText">La cantidad maxima de caracteres es 40</p>
                )}

                <div className="flex flex-col">
                    <LabelTaskForm>Descripción</LabelTaskForm>
                    <textarea
                        {...register("texto", {
                            maxLength: 60,
                        })}
                        className="
                            border-gray-400 focus:outline-none focus:border-gray-800 pl-2 h-[50px] resize-none
                            focus:border-2 border rounded text-gray-600 font-normal hover:border-2"
                        autoComplete="off"></textarea>
                </div>

                {errors.titulo?.type === "maxLength" && (
                    <p className="valErrorText">La cantidad maxima de caracteres es 60</p>
                )}

                <InputContainer>
                    <LabelTaskForm>Fecha de inicio</LabelTaskForm>
                    <input 
                        {...register("fechaInicio", {
                            required: "La tarea requiere una fecha inicial."
                        })}
                        className="
                            border-gray-400 focus:outline-none focus:border-gray-800 pl-2
                            focus:border-2 border rounded text-gray-600 font-normal hover:border-2"
                        type="date" />
                </InputContainer>

                {errors.fechaInicio?.type === "required" && (
                    <p className="valErrorText">{errors.fechaInicio.message}</p>
                )}

                <InputContainer>
                    <LabelTaskForm>Fecha de fin</LabelTaskForm>
                    <input 
                        {...register("fechaFin")}
                        className="
                            border-gray-400 focus:outline-none focus:border-gray-800 pl-2
                            focus:border-2 border rounded text-gray-600 font-normal hover:border-2"
                        type="date" />
                </InputContainer>

                <InputContainer>
                    <LabelTaskForm>hora de inicio</LabelTaskForm>
                    <input
                        {...register("horaInicio", {
                            required: "La tarea requiere una hora inicial."
                        })}
                        className="
                            border-gray-400 focus:outline-none focus:border-gray-800 pl-2
                            focus:border-2 border rounded text-gray-600 font-normal hover:border-2"
                        type="time" />
                </InputContainer>

                {errors.horaInicio?.type === "required" && (
                    <p className="valErrorText">{errors.horaInicio.message}</p>
                )}

                <InputContainer>
                    <LabelTaskForm>hora de fin</LabelTaskForm>
                    <input
                        {...register("horaFin")}
                        className="
                            border-gray-400 focus:outline-none focus:border-gray-800 pl-2
                            focus:border-2 border rounded text-gray-600 font-normal hover:border-2"
                        type="time" />
                </InputContainer>

                <InputContainer>
                    <LabelTaskForm>Enviar recordatorio (1 día antes)</LabelTaskForm>
                    <input
                        {...register("enableSendNotification")}
                        type="checkbox"
                        className="
                            border-gray-400 focus:outline-none focus:border-gray-800 pl-2
                            focus:border-2 border rounded text-gray-600 font-normal hover:border-2"/>
                </InputContainer>

                <div className="flex justify-center">
                    {isLoading
                    ? 
                    <CircularProgress sx={{
                        "--CircularProgress-progressThickness": "4px",
                        "--CircularProgress-trackThickness": "4px",
                        "--CircularProgress-size": "24px"
                    }}/>
                    :
                    <input 
                        className="bg-green-800 rounded font-semibold px-2 text-white py-[2px] text-sm"
                        type="submit"
                        value="crear tarea" />
                    }
                </div>

            </form>

        </div>)}
        </>
    );
}

export function LabelTaskForm({ children }: { children: React.ReactNode }) {
    return (
        <label className="text-gray-600 font-medium">
            { children }
        </label>
    );
}

export function InputContainer({ children }: { children: React.ReactNode }) {
    return (
        <div className="grid grid-cols-2 gap-x-2 justify-between my-2">
            { children }
        </div>

    );
}